import { MeetingContainerAPI, MeetingSourceElement, MeetingSourceElementRef, useMeetingSelector } from "@kalyzee/kast-web-sdk";
import { MeetingSourceExtended } from "../../hooks/meeting";
import React, { createRef, useEffect, useMemo, useRef } from "react";
import { getAudioContextForVolumeAnalyzer } from "../../helpers/audio";
import { MediaSourcesCarousel, MediaCarouselSource } from "@kalyzee/kalyzee-ui";
import { toastError } from "../../helpers/toast";
import { useRefAutoUpdate } from "@kalyzee/kast-web-sdk/web-components";
import { useAppStoreSelector } from "../../contexts/appStore/selectors";
import { useAppStoreDispatch } from "../../contexts/appStore/context";

export interface MediaSourcePickerProps {
  meetingAPI: MeetingContainerAPI;
  display?: boolean;
  sources: MeetingSourceExtended[];
  style?: React.CSSProperties;
  className?: string;
}

(window as any).tracks = {};

const _MediaSourcePicker: React.FC<MediaSourcePickerProps> = ({
  meetingAPI,
  display,
  sources,
  style,
  className,
}) => {
  const meetingAPIRef = useRefAutoUpdate(meetingAPI);
  const sourcesRef = useRef<{ [key: string]: React.RefObject<MeetingSourceElementRef> }>({});

  const [medias] = useMeetingSelector(meetingAPI, "session", session => session?.medias);
  const mediasRef = useRefAutoUpdate(medias);

  const audioEnabled = useAppStoreSelector(state => state.audioStreamEnabled);
  const videoEnabled = useAppStoreSelector(state => state.videoStreamEnabled);

  const dispatch = useAppStoreDispatch();
  const dispatchRef = useRefAutoUpdate(dispatch);

  useEffect(() => {
    const refs = Object.values(sourcesRef.current);
    refs.forEach((r) => {
      const sourceElementRef = r.current;
      if (!sourceElementRef) return;
      const source = sources.find((s) => s === sourceElementRef.getData()?.source);
      if (!source) return;
      sourceElementRef.enableAudio(!!audioEnabled);
      sourceElementRef.enableVideo(!!videoEnabled);
    });
  }, [audioEnabled, videoEnabled, sources]);

  const renderSource = (source: MeetingSourceExtended) => {
    if (!sourcesRef.current[source.name!]) sourcesRef.current[source.name!] = createRef();
    const srcType = source.extra?.type;
    return (
      <MeetingSourceElement
        className="meeting-source-expert"
        style={{ height: "100%", width: "100%", border: "unset", backgroundColor: "unset" }}
        ref={sourcesRef.current[source.name!]}
        key={source.name}
        source={source}
        using
        audioContext={getAudioContextForVolumeAnalyzer()}
        callbacks={{
          track: async (track, data) => {
            const api = meetingAPIRef.current;
            if (!api) return;
            const mediaSession = mediasRef.current?.find((m) => m.name === source.name);
            if (!mediaSession) return;
            const media = data.media;
            await api?.setMediaStream(mediaSession.id, media);
            dispatchRef.current("updateSelfStream", { mediaId: mediaSession.id, stream: media });
          },
          error: (type) => {
            if (type === "audio") {
              toastError("Veuillez autoriser le navigateur à accéder au microphone");
              meetingAPIRef.current?.mute();
            }
            if (type === "video") {
              toastError("Veuillez autoriser le navigateur à accéder à la caméra");
              if (srcType === "screen") meetingAPIRef.current?.disableVideo({ direction: "outgoing", group: "screens" });
              if (srcType === "camera") meetingAPIRef.current?.disableVideo({ direction: "outgoing", group: "cameras" });
            }
          },
        }}
      />
    );
  };

  // When new medias appear on the session (e.g. spectator gets highlighted),
  // attach streams from already-captured MeetingSourceElements
  const attachedMediaIdsRef = useRef(new Set<string>());

  useEffect(() => {
    // if (!medias) return;
    // const api = meetingAPIRef.current;
    // if (!api) return;

    // medias.forEach((mediaSession) => {
    //   if (attachedMediaIdsRef.current.has(mediaSession.id)) return;
    //   const name = mediaSession.name;
    //   if (!name) return;
    //   const sourceRef = sourcesRef.current[name]?.current;
    //   if (!sourceRef) return;
    //   const stream = sourceRef.getData()?.media;
    //   if (!stream) return;

    //   attachedMediaIdsRef.current.add(mediaSession.id);
    //   api.setMediaStream(mediaSession.id, stream).then((res) => {
    //     console.log("Set media stream for session media ", mediaSession.id, res);
    //     dispatch("updateSelfStream", { mediaId: mediaSession.id, stream });
    //   });
    // });

    const handler = meetingAPIRef.current.emissionManager.addEventListener("create", (ovenSession) => {
      // A new ovenmedia session is available
      // Perhaps a setMediaStream was set before it was ready, so we can do it now
      const mediaSession = ovenSession.data.mediaSession;
      if (!mediaSession) return;

      const isAttached = attachedMediaIdsRef.current.has(mediaSession.id);
      if (isAttached) return;

      const mediaName = mediaSession.name;
      if (!mediaName) return;

      const sourceRef = sourcesRef.current[mediaName]?.current;
      if (!sourceRef) return;

      const stream = sourceRef.getData()?.media;
      if (!stream) return;

      attachedMediaIdsRef.current.add(mediaSession.id);
      meetingAPIRef.current.setMediaStream(mediaSession.id, stream).then(() => {
        dispatchRef.current("updateSelfStream", { mediaId: mediaSession.id, stream });
      });
    });

    return () => handler.flush();
  }, []);

  const sourceItems = useMemo(() => {
    return sources.map<MediaCarouselSource>((source) => ({
      id: `media-source-${source.id}`,
      name: source.name,
      content: renderSource(source),
      visible: source.visible,
    }));
  }, [sources]);

  return (
    <MediaSourcesCarousel
      sources={sourceItems}
      display={display}
      style={style}
      className={className}
    />
  );
}

export const MediaSourcePicker = React.memo(_MediaSourcePicker);